/**
 * Cart Upsell Recommendations Custom Element
 * Handles fetching and rendering Malachyte recommendations for cart upsell
 *
 * Features:
 * - Shows skeleton loaders immediately on load
 * - Debounces cart updates (300ms)
 * - Retries failed API calls once
 * - Caches recommendations by cart state
 * - Never clears the section (always shows products, skeletons, or empty message)
 */

import { malachyteAPI } from "@theme/malachyte-api";
import { initLazyImages } from "./lazyImages.js";

/**
 * Cart tiers, keyed by the sorted "+"-joined category signature.
 * Categories: boots, bottoms, tops, apparel (configured in the Malachyte
 * Configuration section). Any cart whose category set is not listed here
 * (e.g. boots+bottoms+tops, or no recognised types) falls back.
 */
const TIER_SIGNATURES = {
  boots: "tier1", // Tier 1 — Boots only
  bottoms: "tier2", // Tier 2 — Bottoms only
  tops: "tier3", // Tier 3 — Tops only
  "boots+bottoms": "tier4", // Tier 4 — Boots + Bottoms
  "apparel+tops": "tier5", // Tier 5 — Tops + Apparel (sorted alphabetically)
  "boots+tops": "tier6", // Tier 6 — Boots + Tops
  "boots+bottoms+tops": "tier7", // Tier 7 — Boots + Bottoms + Tops
};

/**
 * Build a lowercase product-type → category-key lookup from the configured lists.
 * @param {Record<string, string>} [typeKeyLists]
 * @returns {Map<string, string>}
 */
function buildTypeKeyMap(typeKeyLists) {
  const map = new Map();
  if (!typeKeyLists) return map;

  for (const key of Object.keys(typeKeyLists)) {
    const raw = typeKeyLists[key];
    if (!raw) continue;

    for (const type of String(raw).split(",")) {
      const normalized = type.trim().toLowerCase();
      if (normalized) map.set(normalized, key);
    }
  }

  return map;
}

/**
 * Resolve the cart tier from the raw product types present in the cart.
 * Fail fast: a single pass collects the distinct category set, then one O(1)
 * signature lookup decides the tier — no per-scenario evaluation.
 * @param {string} cartRecTypes - Comma-separated raw Shopify product types
 * @param {Record<string, string>} [typeKeyLists]
 * @returns {{ tier: string|null, signature: string, keys: string[] }}
 */
function resolveTier(cartRecTypes, typeKeyLists) {
  const typeKeyMap = buildTypeKeyMap(typeKeyLists);
  const present = new Set();

  for (const raw of String(cartRecTypes || "").split(",")) {
    const normalized = raw.trim().toLowerCase();
    if (!normalized) continue;

    const key = typeKeyMap.get(normalized);
    if (key) present.add(key);
  }

  const signature = Array.from(present).sort().join("+");
  const tier = TIER_SIGNATURES[signature] || null;

  return { tier, signature, keys: Array.from(present) };
}

class CartUpsellRecommendations extends HTMLElement {
  // Static cache: { cartProductIds: { html, handles, timestamp } }
  static #cache = new Map();
  static #fallbackHandles = null; // Fallback products when no recommendations

  #debounceTimer = null;
  #cartUpdateHandler = null;
  #currentCartProductIds = null;
  #isLoading = false;
  #fallbackMode = false;

  connectedCallback() {
    const config = window.MalachyteConfig || {};

    if (config.cartUpsellEnabled === false) {
      // Malachyte off: render the configured fallback collection (if any), else hide.
      const fallbackHandles = Array.isArray(config.fallbackCollectionHandles) ? config.fallbackCollectionHandles : [];
      if (fallbackHandles.length === 0) {
        this.hidden = true;
        return;
      }
      this.#fallbackMode = true;
      this.#currentCartProductIds = this.dataset.cartProductIds || "";
      this.#showSkeletonLoader();
      this.#renderConfiguredFallback();
      this.#setupCartUpdateListener();
      return;
    }

    this.#currentCartProductIds = this.dataset.cartProductIds || "";

    // Always show skeletons initially if we don't have rendered products
    const hasProducts = !!this.querySelector(".cartUpsell__item:not(.animate-pulse)");
    if (!hasProducts) {
      this.#showSkeletonLoader();

      // Check cache first, then load
      if (this.#currentCartProductIds) {
        const cached = CartUpsellRecommendations.#cache.get(this.#currentCartProductIds);
        if (cached) {
          this.#renderHTML(cached.html);
        } else {
          this.#loadRecommendations();
        }
      }
    }

    // Setup cart update listener
    this.#setupCartUpdateListener();
  }

  disconnectedCallback() {
    if (this.#cartUpdateHandler) {
      document.removeEventListener("cart:update", this.#cartUpdateHandler);
    }
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
  }

  #setupCartUpdateListener() {
    this.#cartUpdateHandler = () => {
      // Clear existing debounce timer
      if (this.#debounceTimer) {
        clearTimeout(this.#debounceTimer);
      }

      // Debounce for 300ms to batch multiple cart additions (like GWP products)
      this.#debounceTimer = setTimeout(() => {
        const newCartProductIds = this.dataset.cartProductIds || "";

        // Only reload if cart product IDs actually changed
        if (newCartProductIds !== this.#currentCartProductIds) {
          this.#currentCartProductIds = newCartProductIds;

          // Show skeleton loader before fetching
          this.#showSkeletonLoader();

          // Malachyte off: re-render the fallback collection (re-excludes cart items).
          if (this.#fallbackMode) {
            this.#renderConfiguredFallback();
            return;
          }

          // Check cache first
          const cached = CartUpsellRecommendations.#cache.get(newCartProductIds);
          if (cached) {
            this.#renderHTML(cached.html);
          } else {
            this.#loadRecommendations();
          }
        }
      }, 300);
    };

    document.addEventListener("cart:update", this.#cartUpdateHandler);
  }

  /**
   * Render the merchant-selected fallback collection (Malachyte disabled path).
   * Reuses the same Section Rendering pipeline as Malachyte recs, so cart items
   * are excluded server-side and the card markup is identical.
   */
  async #renderConfiguredFallback() {
    if (this.#isLoading) return;
    this.#isLoading = true;
    try {
      const config = window.MalachyteConfig || {};
      const handles = Array.isArray(config.fallbackCollectionHandles) ? config.fallbackCollectionHandles : [];
      if (handles.length === 0) {
        this.#showEmptyMessage();
        return;
      }
      const html = await this.#fetchSectionHTML(handles);
      this.#renderHTML(html);
    } catch (error) {
      console.error("[Cart Upsell Malachyte] Error rendering fallback collection:", error);
      this.#showEmptyMessage();
    } finally {
      this.#isLoading = false;
    }
  }

  async #loadRecommendations(retryCount = 0) {
    if (this.#isLoading) return;

    this.#isLoading = true;

    const config = window.MalachyteConfig || {};
    const { cartProductIds } = this.dataset;
    const catalog = this.dataset.catalog || config.cartUpsellCatalog || "";
    const maxProducts = parseInt(this.dataset.maxProducts || config.maxProducts || 10);

    // Fail-fast tier detection from cart line-item product types, then map the
    // resolved tier → its Malachyte component ID (fallback when no tier matches).
    const { tier, signature, keys } = resolveTier(this.dataset.cartRecTypes, config.typeKeyLists);
    const tierComponentIds = config.tierComponentIds || {};
    const componentId = (tier && tierComponentIds[tier]) || config.fallbackComponentId || "";

    console.log(
      `[Cart Upsell Malachyte] Tier: ${tier || "fallback"} | ` +
        `categories: [${keys.join(", ") || "none"}] | ` +
        `signature: "${signature || "(empty)"}" | ` +
        `componentId: ${componentId || "(none configured)"}`
    );

    if (!cartProductIds || cartProductIds.trim() === "") {
      this.#isLoading = false;
      this.#showFallbackOrEmpty();
      return;
    }

    if (!componentId) {
      console.warn(
        "[Cart Upsell Malachyte] No component ID for this tier and no fallback configured — skipping Malachyte call."
      );
      this.#isLoading = false;
      this.#showFallbackOrEmpty();
      return;
    }

    try {
      // Get product IDs array
      const productIds = cartProductIds
        .split(",")
        .map(id => id.trim())
        .filter(Boolean);

      if (productIds.length === 0) {
        this.#showFallbackOrEmpty();
        return;
      }

      // Fetch recommendations from Malachyte
      const recommendations = await malachyteAPI.getRecommendations(productIds, {
        k: maxProducts || 10,
        componentId,
        catalog,
      });

      if (!recommendations || recommendations.length === 0) {
        this.#showFallbackOrEmpty();
        return;
      }

      // Extract recommended product IDs
      const recommendedProductIds = recommendations.map(product => product.facts?.id || product.id).filter(Boolean);

      if (recommendedProductIds.length === 0) {
        this.#showFallbackOrEmpty();
        return;
      }

      // Get product handles via GraphQL
      const productHandles = await this.#fetchProductHandles(recommendedProductIds);

      if (!productHandles || productHandles.length === 0) {
        this.#showFallbackOrEmpty();
        return;
      }

      // Fetch section HTML
      const html = await this.#fetchSectionHTML(productHandles);

      // Cache the results
      CartUpsellRecommendations.#cache.set(cartProductIds, {
        html,
        handles: productHandles,
        timestamp: Date.now(),
      });

      // Store as fallback for future use
      CartUpsellRecommendations.#fallbackHandles = productHandles;

      // Render the recommendations
      this.#renderHTML(html);
    } catch (error) {
      console.error("[Cart Upsell Malachyte] Error loading recommendations:", error);

      // Retry once on failure
      if (retryCount < 1) {
        setTimeout(() => this.#loadRecommendations(retryCount + 1), 1000);
      } else {
        this.#showFallbackOrEmpty();
      }
    } finally {
      this.#isLoading = false;
    }
  }

  async #showFallbackOrEmpty() {
    // Try to show fallback products (excluding items already in cart)
    if (CartUpsellRecommendations.#fallbackHandles) {
      try {
        // Get current cart product IDs to exclude them
        const cartProductIds = this.dataset.cartProductIds || "";
        const cartIds = cartProductIds
          .split(",")
          .map(id => id.trim())
          .filter(Boolean);

        // Filter out products already in cart
        let availableHandles = CartUpsellRecommendations.#fallbackHandles;

        if (cartIds.length > 0) {
          // Fetch handles of cart products to exclude them
          const cartHandles = await this.#fetchProductHandles(cartIds);
          const cartHandlesSet = new Set(cartHandles);
          availableHandles = CartUpsellRecommendations.#fallbackHandles.filter(handle => !cartHandlesSet.has(handle));
        }

        if (availableHandles.length > 0) {
          const html = await this.#fetchSectionHTML(availableHandles);
          this.#renderHTML(html);
          return;
        }
      } catch (error) {
        console.error("[Cart Upsell Malachyte] Error showing fallback:", error);
      }
    }

    // No fallback available, show empty message
    this.#showEmptyMessage();
  }

  async #fetchProductHandles(productIds) {
    const gids = productIds.map(id => `gid://shopify/Product/${id}`);

    const query = `
      query($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            handle
          }
        }
      }
    `;

    const variables = { ids: gids };

    if (!window.Shopify?.storefrontAccessToken || !window.Shopify?.shop) {
      throw new Error("Shopify configuration not available");
    }

    const response = await fetch(`https://${window.Shopify.shop}/api/2025-01/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": window.Shopify.storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    return result.data?.nodes?.filter(node => node?.handle).map(node => node.handle) || [];
  }

  async #fetchSectionHTML(productHandles) {
    if (!productHandles || productHandles.length === 0) {
      throw new Error("No product handles provided");
    }

    const firstHandle = productHandles[0];
    const remainingHandles = productHandles.slice(1);

    let url = `/products/${firstHandle}`;
    if (remainingHandles.length > 0) {
      url += `/${remainingHandles.join(",")}`;
    }
    url += `?section_id=cart-upsell-malachyte`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Section fetch failed: ${response.status}`);
    }

    return await response.text();
  }

  /**
   * Admin-configured upsell title (window.MalachyteConfig.cartUpsellTitle).
   * The Section Rendering API can't receive this value, so the rendered section
   * falls back to its own default — we override the title client-side instead.
   * @returns {string}
   */
  #configuredTitle() {
    const t = (window.MalachyteConfig || {}).cartUpsellTitle;
    return typeof t === "string" && t.trim() ? t.trim() : "";
  }

  #renderHTML(htmlText) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, "text/html");
      const newContainer = doc.querySelector("[data-cart-upsell-container]");

      if (!newContainer?.innerHTML?.trim()) {
        this.#showEmptyMessage();
        return;
      }

      const container = this.#getOrCreateContainer();
      container.innerHTML = newContainer.innerHTML;

      // Apply the admin-configured title (overrides the section default).
      const title = this.#configuredTitle();
      if (title) {
        const titleEl = container.querySelector("h4");
        if (titleEl) titleEl.textContent = title;
      }

      // Initialize lazy images
      initLazyImages();

      // Dispatch rendered event
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent("cart-upsell:rendered"));
      });
    } catch (error) {
      console.error("[Cart Upsell Malachyte] Error rendering HTML:", error);
      this.#showEmptyMessage();
    }
  }

  #showSkeletonLoader() {
    const container = this.#getOrCreateContainer();

    container.innerHTML = `
      <h4 class="mb-4 px-4 text-12-regular font-normal text-left" data-cart-upsell-skeleton-title></h4>`;
    const skeletonTitle = container.querySelector("[data-cart-upsell-skeleton-title]");
    if (skeletonTitle) skeletonTitle.textContent = this.#configuredTitle() || "YOU MAY ALSO LIKE";

    container.insertAdjacentHTML(
      "beforeend",
      `
      <div class="cartUpsell__items grid grid-cols-1 gap-4 px-4">
        ${Array(3)
          .fill()
          .map(
            () => `
          <article class="cartUpsell__item p-3 md:p-3 mb-3 bg-[#ffffff] animate-pulse">
            <div class="flex gap-3 relative mb-5">
              <div class="flex-shrink-0 w-20 h-20 md:w-23.5 md:h-23.5 bg-gray-200 rounded"></div>
              <div class="flex-1 flex flex-col min-w-0 gap-2">
                <div class="h-5 bg-gray-200 rounded w-3/4"></div>
                <div class="flex flex-row items-center justify-between gap-2">
                  <div class="h-5 bg-gray-200 rounded w-3/5"></div>
                  <div class="h-5 bg-gray-200 rounded w-16"></div>
                </div>
                <div class="h-9 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
            <div class="h-12 bg-gray-200 w-full"></div>
          </article>
        `
          )
          .join("")}
      </div>
    `
    );
  }

  #showEmptyMessage() {
    const container = this.#getOrCreateContainer();

    container.innerHTML = `
      <h4 class="mb-4 px-4 text-12-regular font-normal text-left" data-cart-upsell-empty-title></h4>
      <div class="cartUpsell__empty text-14-regular text-center py-4 px-4">
        No recommendations available
      </div>
    `;
    const emptyTitle = container.querySelector("[data-cart-upsell-empty-title]");
    if (emptyTitle) emptyTitle.textContent = this.#configuredTitle() || "YOU MAY ALSO LIKE";
  }

  #getOrCreateContainer() {
    let container = this.querySelector("[data-cart-upsell-container]");

    if (!container) {
      container = document.createElement("div");
      container.className = "cartUpsell";
      container.setAttribute("data-cart-upsell-container", "");
      container.setAttribute("data-malachyte", "true");
      this.appendChild(container);
    }

    return container;
  }
}

if (!customElements.get("cart-upsell-recommendations")) {
  customElements.define("cart-upsell-recommendations", CartUpsellRecommendations);
}
